import { Link } from "react-router-dom";
import { ArrowRight, Phone, Siren } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { IMG, PHONE_DISPLAY, PHONE_TEL } from "../data/site";

const URGENCES = [
    {
        title: "Ouverture de porte",
        to: "/ouverture-de-porte",
        text: "Porte claquée, bloquée, coincée ou fermée de l'intérieur : porte fermée à clé, nous crochetons la serrure sans l'abîmer ; porte claquée, la technique d'ouverture avec radio débloque la serrure en moins de 5 minutes.",
    },
    {
        title: "Intervention dans la nuit",
        to: "/devis-contact",
        text: "Perte de clé ou porte coincée qui vous empêche de rentrer ? En moins de 25 minutes, nous sommes au pas de votre porte. Notre service de dépannage intervient 24h/7, même pendant les jours fériés, dans tous les arrondissements de Paris et l'Île-de-France.",
    },
    {
        title: "Déblocage de rideau métallique",
        to: "/rideau-metallique",
        text: "Ces protections pour commerces ou portes de garage exigent un professionnel. Nous prenons en charge le déblocage, la réparation et l'entretien du rideau métallique et de son moteur, en dépannage d'urgence.",
    },
    {
        title: "Dépannage et déblocage porte blindée",
        to: "/porte-blindee",
        text: "Porte blindée bloquée qui ne peut pas s'ouvrir ? Nous intervenons pour la débloquer. La pose et le blindage sont des prestations sur mesure : contactez-nous pour un devis et préparez votre budget.",
    },
    {
        title: "Changement de serrurerie",
        to: "/changement-de-serrure",
        text: "Après un déblocage qui a détérioré la serrure ou une tentative de cambriolage, le remplacement est obligatoire. Notre artisan vous propose un système plus performant et plus résistant.",
    },
    {
        title: "Déblocage coffre-fort",
        to: "/coffre-fort",
        text: "Code oublié, serrure bloquée ? Nous opérons les techniques les plus sûres et efficaces pour ouvrir votre coffre-fort en toute sécurité, et installons votre nouveau dispositif si besoin.",
    },
];

const SITUATIONS = [
    "Perte ou oubli de clé",
    "Déblocage de porte blindée",
    "Dépannage de clé coincée",
    "Rideau métallique bloqué",
    "Stores bloqués",
    "Intervention après cambriolage",
];

const FAQ_SOS = [
    {
        q: "Quel est le délai d'intervention d'un serrurier d'urgence à Paris ?",
        a: "Notre délai moyen est de 25 minutes pour Paris intra-muros. Certains arrondissements centraux (1er au 10ème) sont couverts en 20 minutes en dehors des heures de pointe. La nuit, les délais sont souvent plus courts car le trafic est réduit. Vous recevez une estimation précise dès votre appel.",
    },
    {
        q: "Y a-t-il un supplément tarifaire pour une urgence nocturne ?",
        a: "Un supplément de 20 € à 30 € peut s'appliquer pour les interventions entre 22h et 7h du matin. Les week-ends et jours fériés ne donnent pas lieu à supplément. Ces informations vous sont communiquées au téléphone avant que l'artisan ne se déplace — aucune mauvaise surprise sur place.",
    },
    {
        q: "Que faire si je suis enfermé dehors la nuit ?",
        a: "Appelez-nous immédiatement au 06 25 25 86 19. En attendant : restez dans un endroit éclairé et sécurisé (hall d'immeuble, commerce ouvert, voiture). N'essayez pas de forcer la porte vous-même : vous risquez d'endommager la serrure et d'augmenter le coût de l'intervention. Notre opérateur reste en ligne avec vous si nécessaire.",
    },
    {
        q: "Que faire après une tentative de cambriolage ?",
        a: "Portez plainte auprès de la police avant toute intervention (cela ouvre vos droits à l'assurance). Appelez-nous ensuite : nous sécurisons l'accès dans l'heure, remplaçons le cylindre endommagé et vous remettons un rapport d'intervention pour votre assureur. Si une porte blindée a été forcée, nous évaluons les dommages et vous proposons un renforcement adapté.",
    },
    {
        q: "Pouvez-vous intervenir pour un rideau métallique bloqué en urgence ?",
        a: "Oui. Rideau métallique bloqué en position basse (commerce inaccessible), moteur hors service, lame tordue ou cadenas coincé : nous intervenons en urgence. Nos techniciens disposent des outils pour débloquer manuellement la plupart des mécanismes, même sans électricité.",
    },
];

const PROTOCOLE = [
    {
        etape: "01",
        titre: "Vous appelez",
        texte: "Appelez le 06 25 25 86 19. Notre opérateur qualifie votre situation en 2 minutes, vous confirme le délai et le tarif estimé.",
    },
    {
        etape: "02",
        titre: "Confirmation du devis",
        texte: "Pour les interventions estimées à plus de 150 € TTC, vous recevez un devis oral confirmé par écrit à l'arrivée du technicien. Aucune intervention sans accord de votre part.",
    },
    {
        etape: "03",
        titre: "Intervention en 25 min",
        texte: "L'artisan arrive avec l'outillage adapté à votre type de serrure. Il commence par les techniques non destructives. Il vous informe en temps réel de chaque étape.",
    },
    {
        etape: "04",
        titre: "Facture et rapport",
        texte: "Vous recevez une facture détaillée (SIRET, TVA, pièces + main-d'œuvre séparés) et, si besoin, un rapport d'intervention pour votre assureur.",
    },
];

export default function SosSerrurier() {
    return (
        <div data-testid="sos-serrurier-page">
            <SEO
                title="SOS Serrurier Paris — Urgence 24/7 · Intervention 25 min | 06 25 25 86 19"
                description="SOS Serrurier Paris : dépannage serrurerie en urgence 24h/24 et 7j/7 sur Paris et l'Île-de-France (77, 78, 91, 92, 93, 94, 95). Porte claquée, clé perdue, cambriolage : artisan chez vous en 25 min. Devis gratuit."
                path="/sos-serrurier"
            />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-96 w-96 rounded-full bg-emergency/25 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span className="text-gold">SOS Serrurier</span>
                    </nav>
                    <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-emergency">
                        <Siren className="h-4 w-4" /> Urgence serrurerie — 24/7
                    </p>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={["SOS Serrurier :"]} />
                        <MaskedLines delay={0.45} lines={["prêt à intervenir en urgence."]} lineClassName="text-gold" />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">
                            Vous venez de rentrer du travail et vous avez oublié votre clé ? Vous êtes enfermé dehors ?
                            Notre service SOS Serrurier Paris vous dépanne à tout moment, surtout dans les situations
                            d'urgence. Dès votre appel, vous êtes pris en charge par nos experts — pas de panique.
                        </p>
                        <a
                            href={PHONE_TEL}
                            data-testid="sos-call-button"
                            className="mt-8 inline-flex animate-pulse-ring items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY} — Urgence
                        </a>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            {/* SITUATIONS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
                    <FadeUp>
                        <SectionLabel>Intervention rapide et immédiate</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Votre contact privilégié en cas d'urgence
                        </h2>
                        <p className="mt-6 leading-relaxed text-midnight/70">
                            Nous intervenons dans tous les arrondissements de Paris, tous les jours, 24h/24 et 7j/7,
                            dans la nuit et même les jours fériés grâce à notre service de dépannage en permanence.
                            Notre objectif : garantir votre sécurité dans les plus brefs délais.
                        </p>
                        <img
                            src={IMG("serrurier-paris.paris.webp")}
                            alt="SOS Serrurier Paris — dépannage d'urgence"
                            className="mt-8 aspect-[16/10] w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]"
                            width="1600"
                            height="1000"
                            loading="lazy"
                        />
                    </FadeUp>
                    <div className="grid content-start gap-px bg-midnight/10 sm:grid-cols-2" data-testid="sos-situations">
                        {SITUATIONS.map((s, i) => (
                            <FadeUp key={s} delay={(i % 6) * 0.04} className="bg-white">
                                <div className="flex h-full items-center gap-3 px-5 py-5 text-sm font-bold text-midnight/80">
                                    <Siren className="h-4 w-4 shrink-0 text-emergency" /> {s}
                                </div>
                            </FadeUp>
                        ))}
                        <FadeUp delay={0.2} className="bg-midnight sm:col-span-2">
                            <div className="flex h-full flex-wrap items-center justify-between gap-4 px-5 py-5 text-white">
                                <p className="text-sm font-bold">Urgence en cours ? N'attendez pas.</p>
                                <a href={PHONE_TEL} className="flex items-center gap-2 text-sm font-bold text-gold">
                                    <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                                </a>
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* SERVICES SOS */}
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Nos services SOS</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Un service complet de dépannage serrurerie
                        </h2>
                        <p className="mt-6 max-w-2xl leading-relaxed text-midnight/70">
                            Agréé assurances, nous assurons une prestation fiable et de qualité, réalisée en toute
                            confiance et avec garantie. Demandez un devis SOS serrurerie pour connaître nos tarifs.
                        </p>
                    </FadeUp>
                    <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {URGENCES.map((u, i) => (
                            <FadeUp key={u.title} delay={(i % 3) * 0.08}>
                                <Link
                                    to={u.to}
                                    data-testid={`sos-service-${i}`}
                                    className="group flex h-full flex-col justify-between border border-midnight/10 bg-cloud p-8 transition-all duration-300 hover:-translate-y-2 hover:border-midnight hover:bg-white hover:shadow-[0_28px_60px_-20px_rgba(10,17,40,0.35)]"
                                >
                                    <div>
                                        <span className="font-display text-3xl font-extrabold text-midnight/15">{String(i + 1).padStart(2, "0")}</span>
                                        <h3 className="mt-3 font-display text-xl font-extrabold tracking-tight">{u.title}</h3>
                                        <p className="mt-3 text-sm leading-relaxed text-midnight/60">{u.text}</p>
                                    </div>
                                    <span className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emergency">
                                        En savoir plus <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                                    </span>
                                </Link>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROTOCOLE D'INTERVENTION */}
            <section className="bg-cloud py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Comment ça marche</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Notre protocole d'urgence, étape par étape
                        </h2>
                        <p className="mt-6 max-w-3xl leading-relaxed text-midnight/70">
                            Une situation d'urgence est stressante. Notre processus est conçu pour vous rassurer
                            à chaque étape : tarif annoncé à l'avance, zéro mauvaise surprise, facture conforme
                            pour l'assurance.
                        </p>
                    </FadeUp>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {PROTOCOLE.map((p, i) => (
                            <FadeUp key={p.etape} delay={i * 0.08}>
                                <div className="relative h-full border border-midnight/10 bg-white p-7">
                                    <span className="font-display text-5xl font-extrabold text-midnight/10">{p.etape}</span>
                                    <h3 className="mt-3 font-display text-xl font-extrabold tracking-tight">{p.titre}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-midnight/65">{p.texte}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp delay={0.32}>
                        <div className="mt-12 grid gap-6 lg:grid-cols-3">
                            <div className="bg-midnight text-white p-8 flex flex-col justify-between">
                                <div>
                                    <p className="font-display text-6xl font-extrabold text-[#f97316]">24/7</p>
                                    <p className="mt-2 text-xs font-bold uppercase tracking-wider text-white/50">Disponibilité</p>
                                    <p className="mt-4 text-sm leading-relaxed text-white/70">
                                        Nuits, week-ends, jours fériés (1er janvier, 8 mai, 14 juillet, 25 décembre…).
                                        Notre ligne d'urgence ne ferme jamais.
                                    </p>
                                </div>
                                <a href={PHONE_TEL} className="mt-6 inline-flex items-center gap-2 font-bold text-gold">
                                    <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                                </a>
                            </div>
                            <div className="bg-white border border-midnight/10 p-8 lg:col-span-2">
                                <h3 className="font-display text-xl font-extrabold tracking-tight">
                                    Que faire en attendant le serrurier ?
                                </h3>
                                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-midnight/70">
                                    <li className="flex gap-3">
                                        <Siren className="mt-0.5 h-4 w-4 shrink-0 text-emergency" />
                                        <span><strong className="text-midnight">Restez en lieu sûr</strong> — hall d'immeuble éclairé, commerce ouvert ou voiture. Évitez de rester seul dans la rue la nuit.</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <Siren className="mt-0.5 h-4 w-4 shrink-0 text-emergency" />
                                        <span><strong className="text-midnight">Ne forcez pas la serrure</strong> — un coup de pied ou un tournevis peut transformer une intervention à 60 € en remplacement de serrure à 200 €.</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <Siren className="mt-0.5 h-4 w-4 shrink-0 text-emergency" />
                                        <span><strong className="text-midnight">Voisins et gardien</strong> — vérifiez si un voisin ou le gardien détient un double de clé. C'est la solution la plus rapide et la moins coûteuse.</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <Siren className="mt-0.5 h-4 w-4 shrink-0 text-emergency" />
                                        <span><strong className="text-midnight">Après un cambriolage</strong> — portez plainte au commissariat avant d'appeler un serrurier : c'est obligatoire pour déclencher votre garantie assurance.</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <Siren className="mt-0.5 h-4 w-4 shrink-0 text-emergency" />
                                        <span><strong className="text-midnight">Enfant ou personne âgée enfermé(e)</strong> — signalez-le lors de votre appel : le technicien est redirigé en priorité absolue.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* FAQ SOS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <SectionLabel>Questions fréquentes</SectionLabel>
                    <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                        FAQ — SOS Serrurier Paris
                    </h2>
                </FadeUp>
                <div className="mt-10 space-y-4">
                    {FAQ_SOS.map((item, i) => (
                        <FadeUp key={item.q} delay={i * 0.06}>
                            <details className="group border border-midnight/10 bg-white">
                                <summary className="flex cursor-pointer items-center justify-between px-7 py-5 font-display text-lg font-bold tracking-tight">
                                    {item.q}
                                    <ArrowRight className="h-4 w-4 shrink-0 rotate-90 transition-transform group-open:rotate-[270deg]" />
                                </summary>
                                <div className="px-7 pb-6 pt-1 leading-relaxed text-midnight/70">{item.a}</div>
                            </details>
                        </FadeUp>
                    ))}
                </div>
            </section>


            {/* CTA */}
            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Urgence serrurerie ? Nous arrivons en 25 minutes.
                        </h2>
                        <p className="mt-3 text-white/60">24h/24 — 7j/7, week-ends et jours fériés. Paris & Île-de-France.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="sos-bottom-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
